import { ReactElement, useEffect, useRef, useState } from 'react';
import { HeapCustomEvent, HeapEvent } from 'config';

import { useCart } from 'contexts/Cart';
import CartProduct from 'models/CartProduct';
import HeapAnalytics from 'services/HeapAnalytics';

import { Component as BinIcon } from 'icons/icon-ui--remove.svg';
import { Component as CartIcon } from 'icons/icon-ui--cart-small.svg';
import { Component as CheckIcon } from 'icons/icon-ui--check.svg';

import ServingsToggle from '../shared/ServingsToggle';
import QuantityPicker from '../shared/QuantityPicker';

import useSaveCart from '../../../hooks/useSaveCart';

enum ButtonMode {
  DEFAULT = 'default',
  ADDING = 'adding',
  REMOVING = 'removing',
}

type Props = {
  cartProduct: CartProduct;
  isUpsell?: boolean;
  menuId?: number;
  isProgramPage?: boolean;
};

function RecipeActions({
  cartProduct,
  isUpsell = false,
  menuId,
  isProgramPage = false,
}: Props): ReactElement {
  const {
    cart,
    dispatch,
  } = useCart();
  const { package_products: packageProducts } = cart;
  const {
    currentItem,
    isSoldOut: isCartProductSoldOut,
    hasServingOptions,
    servingOptions,
  } = cartProduct;
  const {
    quantity,
    isMultipleSelectionAllowed,
    sold_out: isCurrentItemSoldOut,
  } = currentItem;
  const {
    title,
    id: skuId,
  } = currentItem.sku;

  /* TODO: we should not have 2 different and contradicting values for an item's sold out
   *  status. Need to clean up all uses of sold out values so the logic is consistent and
   *  we can consolidate to one sold out value.
  */
  const isSoldOut = isProgramPage
    ? isCurrentItemSoldOut || isCartProductSoldOut
    : isCartProductSoldOut;
  const existsInPackageProducts = packageProducts.some((item) => item.sku_id === skuId);
  const maxQuantity = cart.getSelectableQuantity(currentItem);
  const isUpdatable = cart.isUpdatable(cartProduct, isProgramPage);
  const areServingsUpdatable = cart.areServingsUpdatable(cartProduct);

  const [buttonMode, setButtonMode] = useState(ButtonMode.DEFAULT);

  const saveCart = useSaveCart();
  const cartRef = useRef(cart);

  useEffect(() => {
    cartRef.current = cart;
  }, [cart]);

  const setQuantity = (qty) => {
    dispatch({
      type: 'setMenuItemQuantity',
      quantity: qty,
      skuId,
    });
    saveCart();
  };

  const changeServings = (skuIds) => {
    dispatch({
      type: 'setMenuItemQuantities',
      setMaxQty: false,
      skuIds,
    });
    saveCart();
  };

  const addedByRecipeTile = () => {
    dispatch({
      type: 'setAddedOutsideWidget',
      skuId,
    });
  };

  const removedByRecipeTile = () => {
    dispatch({
      type: 'setRemovedOutsideWidget',
      skuId,
    });
  };
  const heapEvents = () => {
    switch (currentItem.mealType) {
      case 'dinner': {
        return {
          add_to_cart: HeapEvent.ADD_TO_CART_DINNER,
          remove_from_cart: HeapEvent.TRASH_CAN_DINNER,
        };
      }
      case 'prepared': {
        return {
          add_to_cart: HeapEvent.ADD_TO_CART_PREPARED,
          remove_from_cart: HeapEvent.TRASH_CAN_PREPARED,
          ticker: HeapEvent.QUANTITY_TICKER_PREPARED,
          ticker_amount: HeapCustomEvent.QUANTITY_TICKER_SUMMARY_PREPARED_AMOUNT,
        };
      }
      case 'breakfast': {
        return {
          add_to_cart: HeapEvent.ADD_TO_CART_BREAKFAST,
          remove_from_cart: HeapEvent.TRASH_CAN_BREAKFAST,
        };
      }
      case 'lunch': {
        return {
          add_to_cart: HeapEvent.ADD_TO_CART_LUNCH,
          remove_from_cart: HeapEvent.TRASH_CAN_LUNCH,
        };
      }
      case 'snack': {
        return {
          add_to_cart: HeapEvent.ADD_TO_CART_PLANTRY,
          remove_from_cart: HeapEvent.TRASH_CAN_PLANTRY,
          ticker: HeapEvent.QUANTITY_TICKER_PLANTRY,
          ticker_amount: HeapCustomEvent.QUANTITY_TICKER_SUMMARY_PLANTRY_AMOUNT,
        };
      }
      default: {
        return {};
      }
    }
  };

  const handleRemoveFromCart = () => {
    setQuantity(0);
    if (existsInPackageProducts) removedByRecipeTile();
    setButtonMode(ButtonMode.REMOVING);

    setTimeout(() => {
      setButtonMode(ButtonMode.DEFAULT);
    }, 1000);
  };

  const handleAddToCart = () => {
    setQuantity(1);
    if (existsInPackageProducts) addedByRecipeTile();
    setButtonMode(ButtonMode.ADDING);

    setTimeout(() => {
      setButtonMode(ButtonMode.DEFAULT);
    }, 1400);

    if (isUpsell) {
      HeapAnalytics.track(
        HeapCustomEvent.EDIT_CART_UPSELL_PRODUCT_ADDED,
        {
          sku: skuId,
          menuId,
        },
      );
    }
  };

  return (
    <div className="c-add-to-cart">
      <button
        type="button"
        id={`add-recipe-${skuId}`}
        className={!isUpdatable
          ? 'c-add-to-cart__disabled'
          : `c-add-to-cart__default ${buttonMode} ${heapEvents().add_to_cart}`}
        style={{ bottom: isProgramPage ? '-.5rem' : '' }}
        disabled={!isUpdatable}
        onClick={handleAddToCart}
      >
        {isSoldOut ? (
          <>
            <span className="sr-only">{`${title} is `}</span>
            Sold Out
          </>
        ) : (
          <>
            {(
              buttonMode === ButtonMode.ADDING
              || buttonMode === ButtonMode.REMOVING
            ) && (
              <>
                <CheckIcon className="c-svg-fill-white-color" width="12" />
                <span className="c-add-to-cart__default-text">
                  <span className="sr-only">{title}</span>
                  &nbsp;
                  {buttonMode === 'adding' ? 'Added!' : 'Removed!'}
                </span>
              </>
            )}

            {buttonMode === ButtonMode.DEFAULT && (
              <>
                <CartIcon className="c-svg-stroke-white-color" width="16" />
                <span className="c-add-to-cart__default-text">
                  Add&nbsp;
                  <span className="sr-only">{title}</span>
                  &nbsp;to cart
                </span>
              </>
            )}
          </>
        )}
      </button>

      <div
        className="c-add-to-cart__success"
        style={{
          borderTop: isProgramPage ? 'none' : '1px solid #ddd',
        }}
      >
        {isMultipleSelectionAllowed && (
          <div className="c-add-to-cart__quantity">
            {!isProgramPage && (
              <span className="c-add-to-cart__quantity-title">
                Quantity
              </span>
            )}
            <QuantityPicker
              quantity={quantity}
              maxQuantity={maxQuantity}
              skuId={skuId}
              onChange={setQuantity}
              heapEventTicker={heapEvents().ticker}
              heapEventTickerAmount={heapEvents().ticker_amount}
            />
          </div>
        )}

        {hasServingOptions && (
          <ServingsToggle
            showTooltip
            cartProduct={cartProduct}
            areServingsUpdatable={areServingsUpdatable}
            onChange={changeServings}
            heapEvents={{
              twoServings: HeapEvent.TWO_SERVING_TOGGLE,
              fourServings: HeapEvent.FOUR_SERVING_TOGGLE,
            }}
          />
        )}

        {!isMultipleSelectionAllowed && !hasServingOptions && (
          <div className="c-add-to-cart__servings-title">
            {`${servingOptions[0]} Servings Meal Kit Dinner`}
          </div>
        )}

        <button
          type="button"
          id={`remove-recipe-${skuId}`}
          className={[
            'c-add-to-cart__remove',
            heapEvents().remove_from_cart,
          ].join(' ')}
          aria-label="Remove {title} from cart"
          onClick={handleRemoveFromCart}
        >
          <BinIcon width="16" />
        </button>
      </div>
    </div>
  );
}

export default RecipeActions;
